import { Component, computed, effect, input, linkedSignal, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { ANALYZER_TYPES, IP_RULE_TYPE_NAMES, IP_RULE_TYPES } from '@ids-constants';
import { CardComponent, CommonTableComponent, CommonToolbarComponent } from '@microsec/components';
import { ActionMenuItem, CommonToolbarConfiguration, CommonToolbarResult } from '@microsec/models';
import { IpRulesetCustomRuleFormComponent } from '../ip-ruleset-custom-rule-form/ip-ruleset-custom-rule-form.component';
import { DELETE_LABEL } from '@microsec/constants';
import { CommonModule } from '@angular/common';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-ruleset-rule-type-field',
  templateUrl: './ruleset-rule-type-field.component.html',
  styleUrls: ['./ruleset-rule-type-field.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    AccordionModule,
    CommonToolbarComponent,
    ToggleSwitchModule,
    CardComponent,
    CommonTableComponent,
  ],
})
export class RulesetRuleTypeFieldComponent extends BaseComponent implements OnInit {
  _rulesetObj: any;

  rulesetObj = input<any>();

  accordionIndex = input<string | number | string[] | number[]>(0);

  displayRulesetObj = linkedSignal<any>(() => {
    const ruleset = this.rulesetObj();
    return ruleset;
  });

  rulesetCols = [
    { field: 'enabled', header: 'Status', width: 10 },
    { field: 'name', header: 'Ruleset', width: 10 },
    { field: 'description', header: 'Description', width: 15 },
  ];

  filterObject: WritableSignal<CommonToolbarResult | null> = signal(null);

  filterConfiguration: CommonToolbarConfiguration = {
    types: ['search'],
    searchPlaceholder: 'Search Ruleset Name...',
  };

  isRulesetEmpty = computed<boolean>(() => {
    const ruleset = this.rulesetObj();
    return (!ruleset?.rule_sets || ruleset?.rule_sets.length === 0) && ruleset?.rule_type !== IP_RULE_TYPES.CUSTOM;
  });

  searchTerm = '';

  customActionsItems: ActionMenuItem[] = [];

  @ViewChild('dt') dt!: CommonTableComponent;

  IP_RULE_TYPE_NAMES = IP_RULE_TYPE_NAMES;

  IP_RULE_TYPES = IP_RULE_TYPES;

  constructor() {
    super();
    effect(() => {
      const values = this.filterObject();
      if (values) {
        if (values?.isSortReset && this.dt?.datatable) {
          this.dt.datatable.sortField = null;
          this.dt.datatable.sortOrder = 1;
          this.dt.datatable.multiSortMeta = null;
          this.dt?.datatable.tableService.onSort(null);
          this.displayRulesetObj.update((obj) => ({
            ...obj,
            rule_sets: this.util.sortObjectArray(this.util.cloneObjectArray(obj.rule_sets || []), 'id'),
          }));
        }
        if (this.searchTerm !== values.search) {
          this.dt?.datatable?.filterGlobal(values.search || '', 'contains');
        }
        this.searchTerm = values.search || '';
      }
    });
  }

  ngOnInit() {
    this.customActionsItems = [
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        command: ({ rowData, index }) => {
          this.openCustomForm(rowData, index);
        },
      },
      {
        label: 'Delete',
        icon: 'fas fa-trash',
        command: ({ rowData, index }) => this.openDeleteConfirmation(rowData, index),
      },
    ];
    if (this.rulesetObj()?.rule_type === IP_RULE_TYPES.CUSTOM) {
      this.rulesetCols = [
        { field: 'enabled', header: 'Status', width: 10 },
        { field: 'name', header: 'Rule Name', width: 15 },
        { field: 'rule', header: 'Rule', width: 15 },
      ];
    }
  }

  openCustomForm(data?: any, index: any = null) {
    const dialog = this.dialogSrv.open(IpRulesetCustomRuleFormComponent, {
      data: { data: !data ? null : data, rbAnalyzerType: ANALYZER_TYPES.RULE_BASED },
      header: `${!data ? 'Create' : 'Edit'} Custom Rule`,
      width: '800px',
      height: 'min-content',
      closeOnEscape: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        if (!rs.id && index === null) {
          // Add new custom rule
          this.displayRulesetObj.update((obj) => ({
            ...obj,
            rule_sets: [...obj.rule_sets, rs],
          }));
        } else {
          //  Edit newly created custom rule or existing custom rule
          if (typeof index === 'number' || typeof index === 'string') {
            this.displayRulesetObj.update((obj) => ({
              ...obj,
              rule_sets: obj.rule_sets.map((item: any, i: number) => (i == index ? { ...rs } : { ...item })),
            }));
          }
        }
      }
    });
  }

  openDeleteConfirmation(data: any, index: any) {
    this.confirm({
      action: DELETE_LABEL,
      objectName: 'Analyzer',
      customContent: `<ul><li>${`Custom Rule ID ${data.name || ''} ${!!data.name ? ` : ${data.name}` : ''}`}</li></ul>`,
      next: () => {
        this.displayRulesetObj.update((obj) => ({
          ...obj,
          rule_sets: obj.rule_sets.filter((_: any, i: number) => i !== index),
        }));
        this.showSuccessMessage(`Deleted Custom Rule ${data.name || ''} successfully`);
      },
    });
  }

  onSwitchChange(checkedValue: any, event: Event) {
    event.stopPropagation();
    this.displayRulesetObj.update((x: any) => ({ ...x, enabled: checkedValue.checked }));
  }

  onRowDataSwitchChange(checkedValue: any, event: Event, index: number) {
    event.stopPropagation();
    this.displayRulesetObj.update((obj) => {
      const updatedRuleSets = [...obj.rule_sets];
      updatedRuleSets[index] = {
        ...updatedRuleSets[index],
        enabled: checkedValue.checked,
      };

      return {
        ...obj,
        rule_sets: updatedRuleSets,
      };
    });
  }

  // // Emit the updated status to the parent
  // toggleStatus(event: any) {
  //   event.stopPropagation(); // Prevent accordion from collapsing
  //   this.enabledStatusChange.emit(this.enableStatus);
  // }

  // // Filter the table based on search term
  // get filteredRules() {
  //   if (!this.searchTerm) return this.values;

  //   return this.values.filter((rule) => Object.values(rule).some((value) => value?.toString().toLowerCase().includes(this.searchTerm.toLowerCase())));
  // }
}
